/**
 * Universal-link / app-link handler for Capacitor native shells.
 *
 * When the OS opens MechaniCar via a deep link (e.g., the Google OAuth
 * redirect at https://mechanicar.app/auth/callback#session_id=...), the
 * App plugin fires `appUrlOpen`. We extract the path + query + hash and
 * push it into React Router so the user lands on the right screen.
 *
 * Renders nothing. No-op on the web.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isNative } from "@/lib/native";

export function DeepLinkListener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return;
    let remove = null;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handler = App.addListener("appUrlOpen", async (event) => {
          try {
            const url = new URL(event.url);
            const slug = `${url.pathname}${url.search}${url.hash}`;
            // Close the in-app/system browser that opened the auth provider.
            // No-op when no browser is open.
            try {
              const { Browser } = await import("@capacitor/browser");
              await Browser.close();
            } catch { /* browser plugin not loaded or nothing to close */ }
            if (slug && slug !== "/") navigate(slug);
          } catch (e) {
            console.warn("Bad deep-link URL:", event.url, e);
          }
        });
        remove = (await handler).remove;
      } catch (e) {
        console.warn("DeepLinkListener init failed:", e);
      }
    })();

    return () => {
      if (typeof remove === "function") {
        try { remove(); } catch { /* no-op */ }
      }
    };
  }, [navigate]);

  return null;
}
